import { useCallback, useState, useRef, useEffect, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import DyanamicDropdown from "./DynamicDropdown";
import { handleSort, formatAssetDisplayName, debounce } from "helpers/utils";
import {
	getSiteAssetRegistersLookup,
	getSiteAssetRegistersLookupCount,
} from "services/clients/sites/siteAssetRegisters";

const MIN_DROPDOWN_WIDTH = 600;

const useStyles = makeStyles()(() => ({
	dropdownContainer: {
		position: "relative",
		width: "100%",
	},
}));

// Sort field mapping: UI column name → backend sort field
const SORT_FIELD_MAP = {
	assetNumber: "assetnumber",
	reference: "reference",
	description: "description",
};

const normalizeAssetData = (item) => {
	if (!item) return item;
	return {
		...item,
		id: item.siteAssetRegisterID || item.id,
		assetNumber: item.assetNumber || "",
		reference:  item.reference ||
                item.assetReferenceName ||
                item.siteAssetReferenceName |"",
		description: item.description || "",
	};
};

const SiteAssetRegisterDropdown = ({
	// Data source (prop-driven mode)
	dataSource,
	count: externalCount,
	page: externalPage,
	onPageChange: externalOnPageChange,
	handleServerSideSearch: externalSearch,
	handleCustomSort: externalSort,
	fetchData: externalFetchData,

	// Self-fetch mode config
	siteId,
	siteAppId,

	// Selection
	selectedValue,
	onChange,
	onClear,

	// Display
	showReference = false,
	customCaptions = {},
	label,
	placeholder,
	showHeader = true,

	// State
	required = false,
	isError = false,
	disabled = false,
	isReadOnly = false,

	// Advanced
	cacheDropDownData = true,
	preloadedSearch,
	isServerSide = true,
}) => {
	const { classes } = useStyles();
	const [inputWidth, setInputWidth] = useState(MIN_DROPDOWN_WIDTH);
	const dropdownRef = useRef(null);

	// Self-fetch mode state
	const isSelfFetch = dataSource == null;
	const [selfAssets, setSelfAssets] = useState([]);
	const [selfPage, setSelfPage] = useState({
		pageNo: 1,
		pageSize: 10,
		sortField: "",
		sortOrder: "",
	});
	const selfPageRef = useRef(selfPage);
	const [selfCount, setSelfCount] = useState(0);

	useEffect(() => {
		selfPageRef.current = selfPage;
	}, [selfPage]);

	// ResizeObserver for dropdown width
	useEffect(() => {
		if (!dropdownRef.current) return;
		const inputElement = dropdownRef.current.querySelector(".inputbox");
		if (!inputElement) return;

		const resizeObserver = new ResizeObserver((entries) => {
			for (let entry of entries) {
				setInputWidth(
					Math.max(entry.contentRect.width, MIN_DROPDOWN_WIDTH)
				);
			}
		});
		resizeObserver.observe(inputElement);
		setInputWidth(Math.max(inputElement.offsetWidth, MIN_DROPDOWN_WIDTH));

		return () => resizeObserver.disconnect();
	}, []);

	// Self-fetch: load assets
	const fetchSelfAssets = useCallback(
		async (
			pageNo = 1,
			prevData = [],
			sortField = "",
			sortOrder = ""
		) => {
			try {
				const result = await getSiteAssetRegistersLookup(
					siteId,
					siteAppId,
					pageNo,
					10,
					"",
					sortField,
					sortOrder
				);
				if (result.status) {
					setSelfAssets([...prevData, ...result.data]);
				}
			} catch {
				// ignore
			}
		},
		[siteId, siteAppId]
	);

	const fetchSelfCount = useCallback(async () => {
		try {
			// Count endpoint requires siteId — derive from session if only siteAppId provided
			let countSiteId = siteId;
			if (!countSiteId) {
				const me = JSON.parse(sessionStorage.getItem("me")) ||
					JSON.parse(localStorage.getItem("me"));
				countSiteId = me?.siteID;
			}
			const result = await getSiteAssetRegistersLookupCount(
				countSiteId,
				siteAppId
			);
			if (result.status) {
				setSelfCount(result.data);
			}
		} catch {
			// ignore
		}
	}, [siteId, siteAppId]);

	const selfFetchData = useCallback(async () => {
		setSelfPage((prev) => ({ ...prev, pageNo: 1, pageSize: 10 }));
		setSelfAssets([]);
		await Promise.all([
			fetchSelfAssets(1, [], selfPage.sortField, selfPage.sortOrder),
			fetchSelfCount(),
		]);
	}, [fetchSelfAssets, fetchSelfCount, selfPage.sortField, selfPage.sortOrder]);

	const selfPageChange = useCallback(
		async (p, prevData) => {
			const { sortField, sortOrder } = selfPageRef.current;
			await fetchSelfAssets(p, prevData, sortField, sortOrder);
			setSelfPage((prev) => ({ ...prev, pageNo: p }));
		},
		[fetchSelfAssets]
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const selfServerSearch = useCallback(
		debounce(async (searchTxt) => {
			if (searchTxt) {
				const response = await getSiteAssetRegistersLookup(
					siteId,
					siteAppId,
					1,
					20,
					searchTxt
				);
				if (response?.status) {
					setSelfPage((prev) => ({ ...prev, pageNo: 1 }));
					setSelfAssets(response.data);
				}
			} else {
				setSelfAssets([]);
				setSelfPage((prev) => ({ ...prev, pageNo: 1 }));
				await fetchSelfAssets(1, []);
			}
		}, 500),
		[siteId, siteAppId, fetchSelfAssets]
	);

	const selfCustomSort = useCallback(
		async (sortField, sortOrder) => {
			const apiField = SORT_FIELD_MAP[sortField] || sortField;
			setSelfPage({
				pageNo: 1,
				pageSize: 10,
				sortField: apiField,
				sortOrder,
			});
			setSelfAssets([]);
			await fetchSelfAssets(1, [], apiField, sortOrder);
		},
		[fetchSelfAssets]
	);

	// Resolve data source
	const assets = isSelfFetch ? selfAssets : dataSource;
	const count = isSelfFetch ? selfCount : externalCount || 0;
	const page = isSelfFetch ? selfPage : externalPage;
	const onPageChange = isSelfFetch ? selfPageChange : externalOnPageChange;
	const serverSearch = isSelfFetch ? selfServerSearch : externalSearch;
	const customSort = isSelfFetch ? selfCustomSort : externalSort;
	const fetchData = isSelfFetch ? selfFetchData : externalFetchData;

	// Normalize data for display
	const normalizedAssets = useMemo(
		() => (assets || []).map(normalizeAssetData),
		[assets]
	);

	// Build columns based on showReference
	const columnWidth = showReference ? "33%" : "50%";

	const dataHeader = useMemo(() => {
		const headers = [
			{
				id: 1,
				name: customCaptions?.asset ?? "Unit Number",
				width: columnWidth,
				sortField: "assetNumber",
			},
		];

		if (showReference) {
			headers.push({
				id: 2,
				name: customCaptions?.assetReference ?? "Functional Location",
				width: columnWidth,
				sortField: "reference",
			});
		}

		headers.push({
			id: 3,
			name: "Description",
			width: showReference ? "34%" : columnWidth,
			sortField: "description",
		});

		return headers;
	}, [showReference, columnWidth, customCaptions]);

	const columns = useMemo(() => {
		const cols = [{ id: 1, name: "assetNumber" }];

		if (showReference) {
			cols.push({ id: 2, name: "reference" });
		}

		cols.push({ id: 3, name: "description" });

		return cols;
	}, [showReference]);

	// Format selected value for display
	const displayValue = useMemo(() => {
		if (!selectedValue) return null;
		const normalized = normalizeAssetData(selectedValue);
		return {
			...normalized,
			displayName: formatAssetDisplayName(normalized, showReference),
		};
	}, [selectedValue, showReference]);

	// Handle sort field mapping for server-side sort
	const handleSortWithMapping = useCallback(
		(sortField, sortOrder) => {
			const apiField = SORT_FIELD_MAP[sortField] || sortField;
			if (customSort) {
				customSort(apiField, sortOrder);
			}
		},
		[customSort]
	);

	return (
		<div className={classes.dropdownContainer} ref={dropdownRef}>
			<DyanamicDropdown
				width="100%"
				dropDownActiveWidth={`${inputWidth}px`}
				dataSource={normalizedAssets}
				dataHeader={dataHeader}
				columns={columns}
				showHeader={showHeader}
				onChange={onChange}
				selectedValue={displayValue}
				selectdValueToshow="displayName"
				count={count}
				handleSort={handleSort}
				handleServerSideSort={handleSortWithMapping}
				handleServerSideSearch={serverSearch}
				isServerSide={isServerSide}
				onPageChange={onPageChange}
				page={page}
				fetchData={fetchData}
				isReadOnly={isReadOnly}
				disabled={disabled}
				showClear={!!onClear}
				onClear={onClear || (() => {})}
				placeholder={
					placeholder ||
					`Select ${customCaptions?.asset || "Asset"}`
				}
				label={label ?? customCaptions?.asset}
				required={required}
				isError={isError}
				cacheDropDownData={cacheDropDownData}
				PreloadedSearch={preloadedSearch}
			/>
		</div>
	);
};

export default SiteAssetRegisterDropdown;
